import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { comparePhpStrings, phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Status reference requires a scalar string argument');
    return phpJsonString(value);
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function arrayReturn(value: unknown, nullable: boolean): ItemFieldRow {
    if (nullable && value === null) return [];
    if (!arrayLike(value)) throw new TypeError('Status lookup returned an invalid PHP array result');
    return clone(value) as ItemFieldRow;
}
/** getCount has an untyped fetchColumn result; preserve PHP's > 0 comparison. */
function positiveCount(value: unknown): boolean {
    if (value == null || value === false) return false;
    if (value === true || arrayLike(value)) return true;
    return isPhpNumeric(value) ? phpJsonFloat(value) > 0 : comparePhpStrings(phpJsonString(value), '0') > 0;
}

export interface StatusReferenceDependencies {
    itemRow: (id: string) => unknown;
    count: (table: 'items' | 'inventory', column: 'item_id' | 'inventory_id', id: string) => unknown;
    fullItem: (id: string, suppressMissingWarning: boolean) => ItemFieldRow | null;
    dynamicItem: (id: string, debugContext: string) => ItemFieldRow;
    determineType: (id: string) => unknown;
    reentry?: (id: string, debugContext: string) => void;
}
export interface StatusReferenceResolvers {
    effect: (statusItemId: unknown, joinedInventoryTemplateId: unknown) => ItemFieldRow;
    origin: (id: unknown, debugContext?: unknown) => ItemFieldRow;
}
/** The request owner must share this instance across recursive status builds.
 * Supplied lookup inputs may throw when unavailable. The origin guard is exact-ID,
 * releases on every exit, and never memoizes a successful or missing result.
 */
export function createStatusReferenceResolvers(deps: StatusReferenceDependencies, initiallyLogged: ItemFieldRow = {}): StatusReferenceResolvers {
    const inFlight = new Set<string>();
    const logged = new Set(Object.entries(initiallyLogged).filter(([, value]) => phpJsonTruthy(value)).map(([id]) => id));
    const full = (id: string, suppress: boolean): ItemFieldRow => arrayReturn(deps.fullItem(id, suppress), true);
    const dynamic = (id: string, context: string): ItemFieldRow => arrayReturn(deps.dynamicItem(id, context), false);
    function effect(statusItemId: unknown, joinedInventoryTemplateId: unknown): ItemFieldRow {
        // Both nullable-string arguments convert before PHP enters the body.
        const id = statusItemId == null ? null : stringArgument(statusItemId);
        const joined = joinedInventoryTemplateId == null ? null : stringArgument(joinedInventoryTemplateId);
        if (validUuid(joined) && phpJsonTruthy(deps.itemRow(joined))) return full(joined, false);
        if (!validUuid(id)) return [];
        if (phpJsonTruthy(deps.itemRow(id))) return full(id, false);
        if (positiveCount(deps.count('inventory', 'inventory_id', id))) {
            const inventory = dynamic(id, 'resolve_status_effect_item_arr_for_layering');
            const template = readInventoryField(inventory, 'inventory_item_id') ?? '';
            if (validUuid(template) && phpJsonTruthy(deps.itemRow(template))) return full(template, false);
        }
        return [];
    }
    function origin(rawId: unknown, debugContext: unknown = ''): ItemFieldRow {
        const id = stringArgument(rawId), context = stringArgument(debugContext);
        if (!validUuid(id)) return [];
        if (inFlight.has(id)) {
            if (!logged.has(id)) { logged.add(id); deps.reentry?.(id, context); }
            return [];
        }
        inFlight.add(id);
        try {
            const type = stringArgument(deps.determineType(id));
            if (['encounter_creature', 'player_character', 'inventory', 'encounter', 'location'].includes(type)) {
                const result = dynamic(id, context); if (phpJsonTruthy(result)) return result;
            }
            if (type === 'item') {
                if (positiveCount(deps.count('items', 'item_id', id))) {
                    const result = full(id, true); if (phpJsonTruthy(result)) return result;
                }
                if (positiveCount(deps.count('inventory', 'inventory_id', id))) return dynamic(id, context);
                return [];
            }
            const result = dynamic(id, context); if (phpJsonTruthy(result)) return result;
            if (positiveCount(deps.count('items', 'item_id', id))) return full(id, true);
            return [];
        } finally { inFlight.delete(id); }
    }
    return { effect, origin };
}
